<template>
  <div v-loading="downloading" id="event-timeline" class="brand-card-lg">
    <div v-if="initialLoading">
      <HeaderLoader />
    </div>
    <div v-else>
      <div class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center justify-between flex-grow">
          <div>
            <p class="title">Event Timeline</p>

            <p class="subtitle mt-2">A feed of all product events.</p>
          </div>
          <el-button v-if="showExport" type="plain" class="border-brand-gray-blue border-2 px-4 rounded-md" @click="downloadDataRecords">
            <span class="text-xs text-brand-blue font-semibold">Export Customers</span>
          </el-button>
        </div>
      </div>
      <div class="flex justify-between flex-grow">
        <div class="flex justify-start">
          <EventSearchFilter @update="onFilterUpdate" />
        </div>
        <div class="flex-col flex space-y-2">
          <div class="flex items-center space-x-2">
            <DatePickerCondensed @change="handleDateChange" :dateRange="[startDate, endDate]"></DatePickerCondensed>
            <!-- <SessionFilter @update="onFilterUpdate" :include="['trial', 'offerType', 'billingInterval', 'segmentId', 'response', 'planId']" /> -->
          </div>
        </div>
      </div>
      <div class="flex justify-start items-center">
        <!-- <el-select placeholder="All Events" filterable clearable multiple class="next-dashboard-select" @change="handleFilterChange" :value="selected">
          <el-option>
          </el-option>
        <el-dropdown v-model="selectedEvents" size="mini">
          <el-checkbox-button v-for="item in eventList" :key="item.name" :label="item.name">
            {{ item.name }}
          </el-checkbox-button>
        </el-dropdown> -->
      </div>
    </div>
    <div>
      <div v-if="loading && !events.length" class="-ml-4">
        <SessionListLoader />
      </div>
      <div v-else-if="events.length" class="grid mt-6 grid-cols-5 gap-x-8">
        <div class="col-span-3">
          <BasicTable
            compressed
            zeroPadding
            :data="events"
            :columns="eventColumns"
            :row-selectable="false"
            idProp="_id"
            @cell-click="onTableCellClick"
            class="cursor-default"
          />

          <!-- <ul class="divide-y divide-brand-sand border-t border-brand-sand pb-4 border-b">
            <li v-for="event in events" :key="event._id" class="">
              <EventCard :event="event" @view="(event) => $emit('view', event)" />
            </li>
          </ul> -->

          <div class="flex justify-center mt-8" v-if="events.length && this.moreEvents">
            <el-button type="primary" class="px-8 rounded-md focus:outline-none" @click="fetchNextPage" v-loading="loading">
              <span class="text-sm text-white font-semibold">Load More ↓</span>
            </el-button>
          </div>
        </div>
        <div class="col-span-2">
          <CustomerRecords :customerRecords="selectedCustomerRecord" :userRecords="selectedUserRecord" />
        </div>
      </div>
      <div v-else class="bg-white h-96"></div>
    </div>
  </div>
</template>

<script>
import { OFFER_TYPE, DISCOUNT_TYPE, LOAD_STATE } from '@/store/shared';
import StatService from '@/api/stats.service';
import { getDateEndpoints, quickDateTime } from '@/helpers/date-utils';
import DatePickerCondensed from '@/components/UI/DatePickerCondensed.vue';
// import SessionFilter from '@/components/CancelAnalytics/SessionFilter.vue';
import BasicTable from '@/components/UI/BasicTable.vue';
import EventCard from '@/components/SubscriptionAnalytics/EventCard.vue';
import EventSearchFilter from '@/components/SubscriptionAnalytics/EventSearchFilter.vue';
import CustomerRecords from '@/components/SubscriptionAnalytics/CustomerRecords.vue';
import { timeDay } from 'd3-time';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import SessionListLoader from '@/components/UI/Loader/SessionListLoader.vue';
import { mapGetters } from 'vuex';
import { DOWNLOAD_DATA_RECORDS, DOWNLOAD_SESSIONS_NEXT } from '@/store/action-types';
import { EVENT_PROP } from '@/helpers/stats-utils';
import eventBus from '@/helpers/event-bus';

const eventCustomerLabel = (event) => {
  if (event.customerRecord) {
    const recordData = event.customerRecord.data || {};
    const { name, email } = recordData;
    return name || email || event.customerRecord.uid || event.customerRecord.customerId;
  }
  return event.uid || event.customerId;
};

const eventUserLabel = (event) => {
  if (event.userRecord) {
    const recordData = event.userRecord.data || {};
    const { name, email } = recordData;
    return name || email || event.userRecord.uid || event.userRecord.userId;
  }
  return null;
};

const eventTimeLabel = (event) => {
  return quickDateTime(event.createdAt);
};

export default {
  name: 'EventTimeline',
  components: {
    // SessionFilter,
    DatePickerCondensed,
    EventCard,
    SessionListLoader,
    HeaderLoader,
    BasicTable,
    EventSearchFilter,
    CustomerRecords,
  },
  props: {},
  async created() {
    await this.fetchData();
    if (this.events.length < 8 && this.daysSinceOrgCreated && this.daysSinceOrgCreated > 90 && !this.searchInput) {
      this.startDate = this.orgCreatedDate;
      await this.fetchData();
    }
    await this.fetchEventList();
    this.initialLoading = false;
  },
  data() {
    const defaultStartDate = timeDay.offset(new Date(), -90);
    const [startDate, endDate] = getDateEndpoints([defaultStartDate, new Date()]);
    return {
      OFFER_TYPE,
      DISCOUNT_TYPE,
      filter: {},
      events: [],
      eventLoadState: LOAD_STATE.UNLOADED,
      downloading: false,
      searchInput: this.$route.query.search || '',
      skip: 0,
      limit: 50,
      moreEvents: true,
      startDate,
      endDate,
      initialLoading: true,
      showDetails: false,
      eventList: [],
      selectedEvents: [],
    };
  },
  computed: {
    ...mapGetters(['daysSinceOrgCreated', 'orgCreatedDate']),
    loading() {
      return this.eventLoadState !== LOAD_STATE.LOADED;
    },
    eventColumns() {
      return [
        { label: 'Event', prop: 'event', align: 'left', formatter: (s) => `<span class="font-semibold">${s}</span>` },
        { label: 'Account', prop: 'customerLabel', align: 'left', valueAccessor: (event) => eventCustomerLabel(event) || eventUserLabel(event) },
        { label: 'User', prop: 'userLabel', align: 'left', valueAccessor: (event) => eventUserLabel(event) },
        { label: 'Time', prop: 'createdAt', formatter: quickDateTime },
      ];
    },
    selectedEvent() {
      if (this.events.length) {
        return this.events[0];
      }
    },
    selectedCustomerRecord() {
      if (this.selectedEvent) {
        return this.selectedEvent.customerRecord;
      }
    },
    selectedUserRecord() {
      if (this.selectedEvent) {
        return this.selectedEvent.userRecord;
      }
    },
    showExport() {
      return this.$store.getters.isAdmin;
    },
  },
  methods: {
    async fetchData() {
      const mappedFilter = Object.keys(this.filter).reduce((acc, k) => {
        if (this.filter[k].length === 1) {
          acc[k] = this.filter[k][0];
        }
        return acc;
      }, {});
      mappedFilter.startDate = this.startDate;
      mappedFilter.endDate = this.endDate;
      if (!mappedFilter.event) {
        mappedFilter['-event'] = 'App Viewed';
      }
      try {
        this.eventLoadState = LOAD_STATE.LOADING;
        const events = await StatService.filteredEvents({ filter: mappedFilter, options: { skip: this.skip, limit: this.limit, withRecords: true } });
        this.events.push(...events);
        this.skip += events.length;
        if (events.length < this.limit) {
          this.moreEvents = false;
        }
        this.eventLoadState = LOAD_STATE.LOADED;
      } catch (e) {
        console.log(e);
        this.eventLoadState = LOAD_STATE.ERROR_LOADING;
      }
    },
    handleDateChange(value) {
      this.startDate = value[0];
      this.endDate = value[1];
      this.resetPagination();
      this.fetchData();
    },
    onFilterUpdate(newFilter) {
      this.filter = newFilter;
      this.resetPagination();
      this.fetchData();
    },
    onTableCellClick(d, prop) {
      const propValue = d[prop];
      if (prop === 'userLabel') {
        // add filter for userUid
        eventBus.$emit('event-filter-add', EVENT_PROP.USER_UID, d[EVENT_PROP.USER_UID]);
      } else if (prop === 'customerLabel') {
        // add filter for customerUid
        eventBus.$emit('event-filter-add', EVENT_PROP.UID, d[EVENT_PROP.UID]);
      } else if (prop === 'event') {
        // add filter for event
        eventBus.$emit('event-filter-add', EVENT_PROP.EVENT_NAME, d[EVENT_PROP.EVENT_NAME]);
      }
    },
    resetPagination() {
      this.events = [];
      this.skip = 0;
      this.moreEvents = true;
    },
    fetchNextPage() {
      this.fetchData();
    },
    search() {
      this.resetPagination();
      this.fetchData();
    },
    async fetchEventList() {
      const counts = await StatService.countEvents({
        filter: { startDate: this.startDate, endDate: this.endDate },
        breakdown: [EVENT_PROP.EVENT_NAME],
      });
      this.eventList = counts.map((c) => ({ name: c.event, count: c.count }));
    },
    async downloadDataRecords() {
      try {
        this.downloading = true;
        const mappedFilter = { entityType: 'CUSTOMER' };
        await this.$store.dispatch(DOWNLOAD_DATA_RECORDS, { filter: mappedFilter });
      } catch (e) {
        console.log(e.toString());
        this.$message({
          message: e.toString(),
          type: 'error',
          duration: 6000,
        });
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>
<style lang="scss">
.activity-stream-search {
  @apply shadow-sm;
  @apply rounded-md;
  width: 20rem;
  .el-input__icon {
    @apply text-brand-gray-blue;
  }
  .el-input__inner {
    @apply border-brand-gray-blue;
    @apply rounded-lg;
    border-width: 2px;
    line-height: 44px;
    height: 44px;
    &::placeholder {
      @apply text-brand-gray-blue;
    }
  }
}
</style>
